import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useState } from "react";
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions
} from "@floating-ui/react";
import { IconArrowLeft, IconArrowRight, IconX } from "@tabler/icons-react";
import { EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { classNames } from "@ct-react/core";
import { ExternalMedia, Image, isImageMedia } from "@shared/graphql";
import "./index.scss";
import "./gallery.scss";

type Props = { className?: string; }
export type GalleryHandler = { open: (items: Array<Image | ExternalMedia>, openIndex?: number) => void };

const Gallery = forwardRef<GalleryHandler, Props>(({ className }: Props, forwardedRef) => {

  const [ open, setOpen ] = useState<boolean>(false);
  const [ initedIndex, setInitedIndex ] = useState<number>();
  const [ currentIndex, setCurrentIndex ] = useState<number>(1);
  const [ previousable, setPreviousable ] = useState<boolean>(false);
  const [ nextable, setNextable ] = useState<boolean>(false);

  const [ content, setContent ] = useState<Array<Image | ExternalMedia>>([]);
  const [ emblaRef, emblaApi ] = useEmblaCarousel({ loop: true });

  useImperativeHandle(forwardedRef, () => ({
    open: (items: Array<Image | ExternalMedia>, openIndex?: number) => {
      setContent(items);
      setOpen(true);
      !!openIndex && setInitedIndex(openIndex);
    }
  }));

  useLayoutEffect(() => {
    const handle = (e: KeyboardEvent) => {
      (e.key === "ArrowRight") && scroll(1);
      (e.key === "ArrowLeft") && scroll(-1);
    }
    open && window.addEventListener("keydown", handle);
    return () => { window.removeEventListener("keydown", handle); };
  }, [ open, emblaApi ]);

  useEffect(() => {
    if (!emblaApi) return;
    !!initedIndex && emblaApi.scrollTo(initedIndex, true);
    emblaApi.on("select", onSelect);
    onSelect(emblaApi);
    return () => emblaApi.destroy();
  }, [ emblaApi ]);

  const { context, refs } = useFloating({
    open,
    onOpenChange: setOpen
  });

  const { getFloatingProps } = useInteractions([
    useDismiss(context)
  ]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setCurrentIndex(emblaApi.selectedScrollSnap() + 1);
    setPreviousable(emblaApi.canScrollPrev());
    setNextable(emblaApi.canScrollNext());
  }, []);

  const scroll = useCallback((direction: -1 | 1) => {
    !!emblaApi && (direction < 0 ? emblaApi.scrollPrev() : emblaApi.scrollNext());
  }, [ emblaApi ]);

  const wrapperClasses = classNames("overlay-content", "gallery", className);

  if (!open)
    return null;

  return (
    <FloatingPortal id="gallery">
      <FloatingOverlay className="overlay-bg gallery" lockScroll />
      <FloatingFocusManager context={context}
                            initialFocus={-1}>
        <div ref={refs.setFloating}
             {...getFloatingProps()}
             className={wrapperClasses}>

          <div ref={emblaRef} className="slider-zone">
            <div className="slider-track">
              {content.map((item, index) => (
                <div key={index} className="slider-item">
                  {isImageMedia(item)
                    ? <img key={index}
                           src={item.assets[0].url}
                           loading="lazy"
                           decoding="async"
                           alt={item.alternativeText?.value} />
                    : <iframe key={index} src={item.url} />
                  }
                </div>)
              )}
            </div>
          </div>

          {content.length > 1 &&
            <div className="nav">
              <button type="button" className="previous"
                      onClick={() => scroll(-1)}
                      disabled={!previousable}>
                <IconArrowLeft />
              </button>
              <button type="button" className="next"
                      onClick={() => scroll(1)}
                      disabled={!nextable}>
                <IconArrowRight />
              </button>
            </div>
          }

          <div className="slider-head">
            {content.length > 1 &&
              <span className="state">{`${currentIndex} / ${content.length}`}</span>
            }
            <button type="button" className="closer"
                    onClick={() => setOpen(false)}>
              <IconX />
            </button>
          </div>

        </div>
      </FloatingFocusManager>
    </FloatingPortal>);

});

export default Gallery;
